import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon, DocumentTextIcon, CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';
import { Textarea, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, LoadingButton } from '@redpanda/ui';
import FormFieldWithError from './forms/FormFieldWithError';
import { toast } from 'react-hot-toast';
import { updatePeriodClosingNotes } from '../services/api';
import { Role } from '@redpanda/types';
import PeriodClosingTimeline from './PeriodClosingTimeline';

interface ClosingEntry {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  createdAt: string;
}

interface PeriodClosing {
  id: string;
  periodId: string;
  type: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REOPENED';
  totalDebits: number;
  totalCredits: number;
  balanceDiff: number;
  notes?: string;
  processedBy: string;
  processedByName?: string;
  startedAt: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  closingEntries?: ClosingEntry[];
  period: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    fiscalYear: {
      id: string;
      name: string;
    };
  };
}

interface PeriodClosingDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  periodClosing: PeriodClosing | null;
  onNotesUpdated?: (updatedClosing: PeriodClosing) => void;
}

interface NotesFormData {
  notes: string;
}

const PeriodClosingDetailDialog: React.FC<PeriodClosingDetailDialogProps> = ({
  isOpen,
  onClose,
  periodClosing,
  onNotesUpdated
}) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { user } = userContext!;
  const userRole = user?.role;

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<NotesFormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditingNotes, setIsEditingNotes] = useState<boolean>(false);

  // 權限檢查：只有管理員可以編輯備註
  const canEditNotes = userRole && [Role.SUPER_ADMIN, Role.ADMIN].includes(userRole);

  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  const handleClose = () => {
    setSubmitError(null);
    setIsEditingNotes(false);
    reset();
    onClose();
  };

  // 使用共用 hook 處理背景點擊關閉
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  // 初始化表單資料
  React.useEffect(() => {
    if (periodClosing && isOpen) {
      setValue('notes', periodClosing.notes || '');
    }
  }, [periodClosing, isOpen, setValue]);

  const onSubmitNotes = async (data: NotesFormData) => {
    if (!periodClosing) return;

    setSubmitError(null);
    setLoading(true);

    try {
      const response = await updatePeriodClosingNotes(periodClosing.id, data.notes);

      setIsEditingNotes(false);
      toast.success(t('periods.toast.noteUpdated'));

      // 通知父組件更新
      if (onNotesUpdated) {
        onNotesUpdated(response.data);
      }
    } catch (error: any) {
      console.error('Notes update failed:', error);
      if (error.response?.data?.error) {
        setSubmitError(error.response.data.error);
      } else {
        setSubmitError(t('common.messages.updateFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditingNotes(true);

    // 簡化處理，不需要捲動功能
    setTimeout(() => {
      const textarea = document.getElementById('notes') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }, 100);
  };

  const handleCancelEdit = () => {
    setIsEditingNotes(false);
    setValue('notes', periodClosing?.notes || '');
    setSubmitError(null);
  };

  // 格式化日期
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-TW');
  };

  // 格式化金額
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // 格式化執行者顯示
  const formatProcessedBy = (periodClosing: PeriodClosing) => {
    return periodClosing.processedByName || periodClosing.processedBy || '系統';
  };

  // 取得狀態顯示
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { label: t('periods.closingStatus.PENDING'), className: 'bg-gray-100 text-gray-800 dark:bg-slate-900 dark:text-gray-300' },
      PROCESSING: { label: t('periods.closingStatus.PROCESSING'), className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      COMPLETED: { label: t('periods.closingStatus.COMPLETED'), className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      FAILED: { label: t('periods.closingStatus.FAILED'), className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
      REOPENED: { label: t('periods.closingStatus.REOPENED'), className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] ||
                   { label: status, className: 'bg-gray-100 text-gray-800 dark:bg-slate-900 dark:text-gray-300' };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // 取得類型顯示
  const getTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
      'MONTHLY': t('periods.closingTypes.MONTHLY'),
      'QUARTERLY': t('periods.closingTypes.QUARTERLY'),
      'YEARLY': t('periods.closingTypes.YEARLY')
    };
    return typeLabels[type] || type;
  };

  if (!isOpen || !periodClosing) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 對話框標題 */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              期間結算詳細資訊
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <div className="p-6 space-y-6">
          {/* 基本資訊區塊 */}
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <CalendarDaysIcon className="h-5 w-5 mr-2 text-brand-600 dark:text-brand-400" />
              基本資訊
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  會計年度
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {periodClosing.period.fiscalYear.name}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  期間名稱
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {periodClosing.period.name}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  結算類型
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {getTypeLabel(periodClosing.type)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  狀態
                </label>
                <div>
                  {getStatusBadge(periodClosing.status)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  期間範圍
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {formatDate(periodClosing.period.startDate)} ~ {formatDate(periodClosing.period.endDate)}
                </p>
              </div>
            </div>
          </div>

          {/* 執行資訊區塊 */}
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-brand-600 dark:text-brand-400" />
              執行資訊
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  開始時間
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {formatDate(periodClosing.startedAt)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  完成時間
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {formatDate(periodClosing.completedAt)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  執行者
                </label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {formatProcessedBy(periodClosing)}
                </p>
              </div>
            </div>
          </div>

          {/* 試算表資訊區塊 */}
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              試算表資訊
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                  借方總額
                </label>
                <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                  NT$ {formatAmount(periodClosing.totalDebits)}
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                  貸方總額
                </label>
                <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  NT$ {formatAmount(periodClosing.totalCredits)}
                </p>
              </div>

              <div className={`p-3 rounded ${
                Math.abs(periodClosing.balanceDiff) < 0.01
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                <label className={`block text-sm font-medium mb-1 ${
                  Math.abs(periodClosing.balanceDiff) < 0.01
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  差異金額
                </label>
                <p className={`text-lg font-semibold ${
                  Math.abs(periodClosing.balanceDiff) < 0.01
                    ? 'text-green-900 dark:text-green-100'
                    : 'text-red-900 dark:text-red-100'
                }`}>
                  NT$ {formatAmount(Math.abs(periodClosing.balanceDiff))}
                  {Math.abs(periodClosing.balanceDiff) < 0.01 && ' (平衡)'}
                </p>
              </div>
            </div>
          </div>

          {/* 結算分錄區塊 */}
          {periodClosing.closingEntries && periodClosing.closingEntries.length > 0 && (
            <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-brand-600 dark:text-brand-400" />
                結算分錄 ({periodClosing.closingEntries.length} 筆)
              </h3>

              <div className="bg-white dark:bg-slate-800 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/6">科目代號</TableHead>
                        <TableHead className="w-1/4">科目名稱</TableHead>
                        <TableHead className="w-1/6">借方金額</TableHead>
                        <TableHead className="w-1/6">貸方金額</TableHead>
                        <TableHead className="w-1/4">說明</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {periodClosing.closingEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-mono text-sm">{entry.accountCode}</TableCell>
                          <TableCell>{entry.accountName}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {entry.debitAmount > 0 ? `NT$ ${formatAmount(entry.debitAmount)}` : '-'}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {entry.creditAmount > 0 ? `NT$ ${formatAmount(entry.creditAmount)}` : '-'}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600 dark:text-gray-300">
                            {entry.description}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* 備註區塊 */}
          <div
            className={`rounded-lg p-4 transition-all duration-200 ${
              isEditingNotes
                ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800'
                : 'bg-gray-50 dark:bg-slate-700'
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                備註
              </h3>
              {!isEditingNotes && canEditNotes && (
                <button
                  onClick={handleEditClick}
                  className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 text-sm font-medium"
                >
                  編輯
                </button>
              )}
            </div>

            {!isEditingNotes ? (
              <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                {periodClosing.notes || '無備註'}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmitNotes)} className="space-y-4">
                <FormFieldWithError
                  label="備註內容"
                  error={errors.notes?.message}
                  htmlFor="notes"
                >
                  <Textarea
                    id="notes"
                    rows={4}
                    placeholder={t('common.placeholders.enterNotes')}
                    {...register('notes', {
                      maxLength: { value: 1000, message: '備註不能超過 1000 字元' }
                    })}
                    variant={errors.notes ? "error" : "default"}
                  />
                </FormFieldWithError>

                {submitError && (
                  <div className="text-red-600 dark:text-red-400 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-gray-300 bg-white dark:bg-slate-600 border border-slate-300 dark:border-slate-500 rounded-md shadow-sm hover:bg-slate-50 dark:hover:bg-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500"
                  >
                    {t('common.actions.cancel')}
                  </button>
                  <LoadingButton
                    loading={loading}
                    type="submit"
                    className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
                  >
                    {t('common.actions.save')}
                  </LoadingButton>
                </div>
              </form>
            )}
          </div>

          {/* 歷史時間軸區塊 */}
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-brand-600 dark:text-brand-400" />
              結算歷史軌跡
            </h3>
            <PeriodClosingTimeline
              periodClosingId={periodClosing.id}
              className="max-h-[36rem] overflow-y-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodClosingDetailDialog;